import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, BookOpen, Monitor, ScrollText, BarChart2,
  GitBranch, Users, Tag, Settings, FlaskConical, Upload,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";

export function Sidebar() {
  const { role } = useAuth();
  const navigate = useNavigate();

  const isAdmin = role === "FleetAdmin";
  const isRM = role === "ReleaseManager";
  const isSupport = role === "SupportEngineer";

  return (
    <aside className="flex flex-col w-56 min-h-screen bg-white border-r border-slate-200 shrink-0">
      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-5">

        {/* NAVIGATION */}
        <div>
          <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Navigation</p>
          <SideItem to="/" icon={Home} label="Hub" end />
        </div>

        {/* INSTALL BASELINE */}
        {(isAdmin || isRM) && (
          <div>
            <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Install Baseline</p>
            <SideItem to="/firmware" icon={BookOpen} label="Baseline Library" badge={3} badgeColor="teal" />
            <SideItem to="/firmware/upload" icon={Upload} label="Upload Baseline" />
          </div>
        )}

        {/* FLEET */}
        <div>
          <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Fleet</p>
          {(isAdmin || isSupport) && (
            <SideItem to="/devices" icon={Monitor} label="Fleet Overview" badge={12} badgeColor="amber" />
          )}
          {(isAdmin || isRM) && (
            <SideItem to="/rollouts" icon={GitBranch} label="Rollouts" />
          )}
          {(isAdmin || isSupport) && (
            <SideItem to="/admin/audit" icon={ScrollText} label="Audit Log" iconColor="text-orange-500" />
          )}
        </div>

        {/* REPORTS */}
        <div>
          <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Reports</p>
          <SideItem to="/telemetry" icon={BarChart2} label="Reports" />
        </div>

        {/* ADMIN */}
        {isAdmin && (
          <div>
            <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Administration</p>
            <SideItem to="/admin/users" icon={Users} label="User Management" />
            <SideItem to="/admin/segments" icon={Tag} label="Segments" />
            <SideItem to="/admin/config" icon={Settings} label="System Config" />
          </div>
        )}

        {/* INC 2 */}
        <div>
          <p className="px-2 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">INC 2 — Future</p>
          <SideItem to="/inc2/logs" icon={FlaskConical} label="Log Triage" future />
        </div>

      </nav>
    </aside>
  );
}

function SideItem({ to, icon: Icon, label, end, badge, badgeColor, iconColor, future }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors mb-0.5 group",
          isActive
            ? "bg-teal-50 text-teal-800 font-semibold border-l-2 border-teal-600 pl-[6px]"
            : future
            ? "text-slate-400 hover:text-slate-500 hover:bg-slate-50"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium"
        )
      }
    >
      <Icon className={cn("h-4 w-4 shrink-0", iconColor)} />
      <span className="flex-1 truncate">{label}</span>
      {badge != null && (
        <span className={cn(
          "text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center",
          badgeColor === "teal" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"
        )}>
          {badge}
        </span>
      )}
      {future && (
        <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">INC2</span>
      )}
    </NavLink>
  );
}
